import { useForm } from '@mantine/form';

import { IconSearch, IconUser } from '@tabler/icons-react';

import { Alert, Button, Center, Drawer, Group, Loader, Select, Stack, Text } from '@optiro/ui';

import { useAccounts } from '@/hooks/useAccounts';
import { useChangeResourceable } from '@/hooks/useChangeResourceable';
import { usePartners } from '@/hooks/usePartners/usePartners';
import { useResource } from '@/hooks/useResource';
import { Project, Resource } from '@/types/types';

type ResourcePlaceholderAssignProps = {
  opened: boolean;
  resourceId: Resource['id'];
  projectId?: Project['id'];
  onClose: () => void;
  onSuccess?: () => void;
};

function ResourcePlaceholderAssign({
  opened,
  onClose,
  resourceId,
  projectId,
  onSuccess,
}: ResourcePlaceholderAssignProps) {
  const { data: resource, isLoading: resourceLoading } = useResource({
    id: resourceId,
    projectId,
  });
  const { data: accounts } = useAccounts();
  const { data: partners } = usePartners();
  const { mutate, isError, isPending, error } = useChangeResourceable(
    resourceId,
    resource?.project_id || projectId,
    resource?.task_id
  );

  // Group accounts by category
  const accountOptions = accounts
    ? Object.values(
        accounts.reduce<
          Record<string, { group: string; items: { value: string; label: string }[] }>
        >((acc, account) => {
          const groupName = account.company_account_category?.name || 'Ukendt kategori';
          if (!acc[groupName]) {
            acc[groupName] = { group: groupName, items: [] };
          }
          acc[groupName].items.push({
            value: `${account.id}`,
            label: account.name,
          });
          return acc;
        }, {})
      )
    : [];

  // Group partners by category
  const partnerOptions = partners
    ? Object.values(
        partners.reduce<
          Record<string, { group: string; items: { value: string; label: string }[] }>
        >((acc, partner) => {
          const groupName = partner.company_account_category?.name || 'Ukendt kategori';
          if (!acc[groupName]) {
            acc[groupName] = { group: groupName, items: [] };
          }
          acc[groupName].items.push({
            value: `${partner.id}`,
            label: partner.name,
          });
          return acc;
        }, {})
      )
    : [];

  const form = useForm({
    initialValues: {
      accountId: '',
      partnerId: '',
    },
    validate: (values) => {
      if (!values.accountId && !values.partnerId) {
        return {
          accountId: 'Du skal vælge enten en medarbejder eller en partner',
        };
      }
      return {};
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    // Determine which type was selected
    if (values.accountId) {
      mutate(
        {
          resourceable_type: 'Account',
          resourceable_id: values.accountId,
        },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
        }
      );
    } else if (values.partnerId) {
      mutate(
        {
          resourceable_type: 'Partner',
          resourceable_id: values.partnerId,
        },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
        }
      );
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Clear the other field when one is selected
  const handleAccountChange = (value: string | null) => {
    form.setFieldValue('accountId', value || '');
    if (value) {
      form.setFieldValue('partnerId', '');
    }
    form.clearErrors();
  };

  const handlePartnerChange = (value: string | null) => {
    form.setFieldValue('partnerId', value || '');
    if (value) {
      form.setFieldValue('accountId', '');
    }
    form.clearErrors();
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="md"
      withOverlay={false}
      styles={{
        header: {
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '1rem',
        },
        body: {
          padding: 0,
        },
      }}
      title={
        <Group gap="sm">
          <IconUser size={20} />
          <Text>Tildel ressource</Text>
        </Group>
      }
    >
      {resourceLoading ? (
        <Center p="xl">
          <Loader color="#3c735b" />
        </Center>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg" p="xl">
            <Text size="sm" c="dimmed">
              Erstat pladsholderen med en medarbejder eller partner
            </Text>

            <Select
              label="Medarbejder"
              description="Vælg en medarbejder fra din organisation"
              data={accountOptions}
              size="md"
              placeholder="Vælg medarbejder"
              leftSection={<IconSearch size={16} />}
              clearable
              searchable
              disabled={!!form.values.partnerId}
              value={form.values.accountId}
              onChange={handleAccountChange}
              error={form.errors.accountId}
            />

            <Select
              label="Partner"
              description="Vælg en ekstern partner"
              data={partnerOptions}
              size="md"
              placeholder="Vælg partner"
              leftSection={<IconSearch size={16} />}
              clearable
              searchable
              disabled={!!form.values.accountId}
              value={form.values.partnerId}
              onChange={handlePartnerChange}
            />

            {isError && (
              <Alert color="red" title="Fejl">
                {(
                  error as Error & {
                    response?: { data?: { message?: string } };
                  }
                )?.response?.data?.message ||
                  'Ressourcen har allerede en booking for denne opgave.'}
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={handleClose}>
                Annuller
              </Button>
              <Button type="submit" loading={isPending} style={{ backgroundColor: '#3c735b' }}>
                Tildel ressource
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Drawer>
  );
}

export { ResourcePlaceholderAssign };
